<template>
  <div class="agent-container">
    <div
      class="agent-panel"
      v-if="state.hasInitPanel"
      v-show="state.showPanel"
      :style="{ width: state.panelWidth + 'px' }"
      :class="{ 'is-dragging': state.isDragging }"
    >
      <div class="resize-handle" @mousedown="handleMouseDown">
        <div class="resize-line"></div>
      </div>
      <div class="agent-panel-header">
        <a-typography-text strong>
          智能助手
        </a-typography-text>
        <CloseOutlined @click="handleClosePanel" />
      </div>
      <div v-if="state.isLoading" class="flex aic jcc !min-h-[200px]">
        <a-spin />
      </div>
      <iframe :src="state.agentUrl" @load="handleIframeLoad" />
    </div>
    <div
      ref="agentIconRef"
      class="agent-icon"
      :class="{ hide: state.hideAgentIcon }"
      v-show="!state.showPanel"
      @click="openAgentPanel"
      v-draggable="{ onDragMove: handleDragMove }"
    >
      <CloseCircleFilled class="close-icon" @click="handleCloseIcon" />
      <RobotFilled />
    </div>
  </div>
</template>
<script setup>
import { reactive, ref, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import {
  RobotFilled,
  CloseOutlined,
  CloseCircleFilled
} from '@ant-design/icons-vue'

const store = useStore()
const agentIconRef = ref(null)
const params = {
  uuid: 'portal_agent_id_' + store.state.user.userInfo.userId,
  conversation: {
    userAvatar: store.state.user.userInfo.avatar,
    needCache: true,
    storageType: 'localStorage'
  }
}

const stringParams = JSON.stringify(params)
const configValue = encodeURIComponent(btoa(stringParams))

const state = reactive({
  hideAgentIcon: localStorage.getItem('ShowPortalAgent') === 'false',
  showPanel: false,
  panelWidth: 300, // 默认宽度
  isDragging: false,
  startX: 0,
  startWidth: 0,
  agentUrl:
    'https://ae.feishu.cn/cui#appkey=a_e9efc0ff83934219861cbd44a84e4955&config=' +
    configValue,
  hasInitPanel: false,
  isLoading: true
})

const handleCloseIcon = e => {
  e.stopPropagation()
  e.preventDefault()
  state.hideAgentIcon = true
  if (agentIconRef.value) {
    agentIconRef.value.style.removeProperty('top')
    agentIconRef.value.style.removeProperty('left')
  }
  localStorage.setItem('ShowPortalAgent', 'false')
}

const openAgentPanel = e => {
  // 检查是否发生了拖拽
  if (e.currentTarget.dataset.isMoved === 'true') {
    e.stopPropagation()
    e.preventDefault()
    return
  }
  state.showPanel = true
  state.hasInitPanel = true
}

const handleClosePanel = () => {
  state.showPanel = false
}

// 拖拽开始
const handleMouseDown = e => {
  state.isDragging = true
  state.startX = e.clientX
  state.startWidth = state.panelWidth
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
  document.body.style.cursor = 'ew-resize'
  document.body.style.userSelect = 'none'
  e.preventDefault()
}

// 拖拽中
const handleMouseMove = e => {
  if (!state.isDragging) return
  const deltaX = e.clientX - state.startX // 向右拖拽为正值
  const newWidth = state.startWidth - deltaX // 向右拖拽减小宽度（因为面板在右侧）
  // 限制最小和最大宽度
  const minWidth = 250
  const maxWidth = Math.min(800, window.innerWidth * 0.8)
  state.panelWidth = Math.max(minWidth, Math.min(maxWidth, newWidth))
}

// 拖拽结束
const handleMouseUp = () => {
  state.isDragging = false
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
}

const handleIframeLoad = () => {
  state.isLoading = false
}

const handleDragMove = () => {
  state.hideAgentIcon = false
  localStorage.removeItem('ShowPortalAgent')
}

// 清理事件监听器
onUnmounted(() => {
  // 清理面板拖拽事件
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
  // 重置body样式
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
  // 重置拖拽状态
  state.isDragging = false
})
</script>
<style lang="scss" scoped>
.agent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 30px;
  transform: scale(1);
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4), 0 2px 8px rgba(0, 0, 0, 0.1);
  position: fixed;
  bottom: 56px;
  right: 28px;
  z-index: 1000;
  overflow: visible;
  cursor: pointer;

  &.hide {
    right: -36px;
    bottom: 56px;
    transition: all 0.3s ease;

    &:hover {
      right: 10px;
    }
  }

  // 光圈闪烁效果
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    background: linear-gradient(
      135deg,
      rgba(102, 126, 234, 0.3),
      rgba(118, 75, 162, 0.3)
    );
    border-radius: 34px;
    z-index: -1;
    animation: pulse-ring 2s infinite;
    opacity: 0.8;
    border: 1px solid rgba(102, 126, 234, 0.2);
  }

  // 内部光效
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 20px;
    height: 20px;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.3) 0%,
      transparent 70%
    );
    border-radius: 50%;
    transform: translate(-50%, -50%);
    animation: inner-glow 3s ease-in-out infinite;
  }

  &:hover {
    transform: scale(1.05);
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    box-shadow: 0 8px 24px rgba(79, 172, 254, 0.5),
      0 4px 12px rgba(0, 0, 0, 0.15);
    &::before {
      background: linear-gradient(
        135deg,
        rgba(79, 172, 254, 0.4),
        rgba(0, 242, 254, 0.4)
      );
      animation: pulse-ring-hover 1.5s infinite;
      border: 1px solid rgba(79, 172, 254, 0.3);
      top: -6px;
      left: -6px;
      right: -6px;
      bottom: -6px;
      border-radius: 36px;
    }
    .close-icon {
      display: block;
    }
  }

  .anticon {
    color: #fff;
    transition: transform 0.3s ease;
    position: relative;
    font-size: 18px;
    z-index: 2;
  }

  .close-icon {
    display: none;
    position: absolute !important;
    top: -9px;
    right: -7px;
    color: #8d8d8d !important;
    font-size: 12px !important;
    font-weight: bold;
    cursor: pointer;
  }

  // 拖拽状态样式
  &.dragging {
    box-shadow: 0 12px 32px rgba(102, 126, 234, 0.6),
      0 6px 16px rgba(0, 0, 0, 0.2);
    z-index: 1001;
    filter: brightness(1.1) scale(1.05);
    &::before {
      animation: pulse-ring-hover 1s infinite;
      opacity: 1;
    }
  }
}

// 光圈脉冲动画
@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.3;
  }
  100% {
    transform: scale(1);
    opacity: 0.6;
  }
}

// 悬停时的光圈动画
@keyframes pulse-ring-hover {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.4;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
}

// 内部光效动画
@keyframes inner-glow {
  0%,
  100% {
    opacity: 0.3;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1.2);
  }
}

.agent-panel {
  position: relative;
  margin: 10px 10px 10px 0;
  height: calc(100vh - 70px);
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;

  &.is-dragging {
    iframe {
      pointer-events: none;
    }
  }

  .resize-handle {
    position: absolute;
    left: 0;
    top: 0;
    width: 6px;
    height: 100%;
    cursor: ew-resize;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(24, 144, 255, 0.1);
    }

    .resize-line {
      width: 2px;
      height: 40px;
      background-color: #d9d9d9;
      border-radius: 1px;
      transition: all 0.2s ease;
    }

    &:hover .resize-line {
      background-color: #1890ff;
      height: 60px;
    }
  }

  .agent-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px 10px 22px; // 左侧增加padding为拖拽区域让路
    border-bottom: 1px solid #e8e8e8;
    background-color: #fafafa;
  }

  iframe {
    display: block !important;
    height: calc(100% - 43px);
    width: 100%;
    border: none;
    padding-left: 6px; // 为拖拽区域让路
    box-sizing: border-box;
  }

  .anticon-close {
    color: #a7a7a7;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #1890ff;
    }
  }
}
</style>
